import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, Eye, ImagePlus, Trash2, Loader2 } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { api, API } from "../api";

const readAsDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

/** MASTER: mandatory update — min version per platform, lock-screen image, store links. */
export const ForceUpdateCard = ({ t }) => {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ active: false, min_ios: "", min_android: "", ios_url: "", android_url: "" });
  const [image, setImage] = useState(undefined); // undefined = keep, "" = remove, dataURL = new
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);

  const load = (d) => {
    setDoc(d);
    setForm({ active: !!d.active, min_ios: d.min_ios || "", min_android: d.min_android || "", ios_url: d.ios_url || "", android_url: d.android_url || "" });
    setImage(undefined);
  };
  useEffect(() => { api.get("/app/force-update").then((r) => load(r.data)).catch(() => {}); }, []);

  const currentImg = image === undefined
    ? (doc?.has_image ? `${API.replace(/\/api$/, "")}/api/app/force-update/image?v=${doc.image_v || 0}` : null)
    : (image || null);

  const save = async () => {
    if (form.active && !form.min_ios.trim() && !form.min_android.trim()) { toast.error(t("force_update_need_min")); return; }
    setSaving(true);
    try {
      const r = await api.put("/app/force-update", { ...form, ...(image !== undefined ? { image_base64: image } : {}) });
      load(r.data);
      toast.success(form.active ? t("force_update_saved_on") : t("force_update_saved_off"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("something_wrong")); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Card className="p-4 space-y-3 border-rose-500/30" data-testid="force-update-card">
      <div className="flex items-center gap-2 font-display font-semibold text-rose-500"><Lock className="h-4 w-4" /> {t("force_update_title_admin")}</div>
      <p className="text-xs text-muted-foreground">{t("force_update_help")}</p>
      <label className="flex items-center gap-2 text-sm font-bold text-rose-500 cursor-pointer">
        <Checkbox checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: !!v }))} data-testid="force-update-active" />
        <Lock className="h-4 w-4" /> {t("force_update_active")}
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] uppercase text-muted-foreground">{t("force_update_min_ios")}</Label>
          <Input value={form.min_ios} onChange={set("min_ios")} placeholder="1.35" className="mt-1 font-mono-plex" data-testid="force-update-min-ios" />
        </div>
        <div>
          <Label className="text-[11px] uppercase text-muted-foreground">{t("force_update_min_android")}</Label>
          <Input value={form.min_android} onChange={set("min_android")} placeholder="1.35" className="mt-1 font-mono-plex" data-testid="force-update-min-android" />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">{t("force_update_min_hint")}</p>
      <div>
        <Label className="text-[11px] uppercase text-muted-foreground">{t("force_update_image")}</Label>
        <div className="flex items-center gap-3 mt-1">
          {currentImg ? (
            <img src={currentImg} alt="" className="h-28 w-28 rounded-xl object-cover border" data-testid="force-update-image-preview" />
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="h-28 w-28 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-xs text-muted-foreground hover:bg-muted" data-testid="force-update-image-add">
              <ImagePlus className="h-6 w-6 mb-1" /> {t("force_update_image_add")}
            </button>
          )}
          <div className="flex flex-col gap-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="force-update-image-change"><ImagePlus className="h-4 w-4 mr-1" /> {currentImg ? t("change") : t("force_update_image_add")}</Button>
            {currentImg && <Button size="sm" variant="outline" className="text-destructive" onClick={() => setImage("")} data-testid="force-update-image-remove"><Trash2 className="h-4 w-4 mr-1" /> {t("remove")}</Button>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="force-update-image-input"
            onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; if (f.size > 2 * 1024 * 1024) { toast.error(t("force_update_image_big")); return; } setImage(await readAsDataUrl(f)); }} />
        </div>
      </div>
      <div>
        <Label className="text-[11px] uppercase text-muted-foreground">{t("force_update_ios_url")}</Label>
        <Input value={form.ios_url} onChange={set("ios_url")} placeholder={doc?.store_urls?.ios || "https://apps.apple.com/…"} className="mt-1 text-xs" data-testid="force-update-ios-url" />
      </div>
      <div>
        <Label className="text-[11px] uppercase text-muted-foreground">{t("force_update_android_url")}</Label>
        <Input value={form.android_url} onChange={set("android_url")} placeholder={doc?.store_urls?.android || "https://play.google.com/…"} className="mt-1 text-xs" data-testid="force-update-android-url" />
      </div>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={() => setPreview(true)} data-testid="force-update-preview-btn"><Eye className="h-4 w-4 mr-1" /> {t("preview")}</Button>
        <Button className="flex-[2] bg-rose-500 hover:bg-rose-600 text-white" onClick={save} disabled={saving} data-testid="force-update-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Lock className="h-4 w-4 mr-1" />} {form.active ? t("force_update_save_on") : t("save")}
        </Button>
      </div>
      {preview && (
        <div className="fixed inset-0 z-[1000] bg-[#0b1220] text-white flex flex-col items-center justify-center px-6" data-testid="force-update-preview" onClick={() => setPreview(false)}>
          <div className="w-full max-w-sm aspect-square rounded-3xl overflow-hidden bg-white/5 flex items-center justify-center">
            {currentImg ? <img src={currentImg} alt="" className="w-full h-full object-cover" /> : <div className="p-8 text-center"><div className="font-display text-2xl font-bold">{t("force_update_title")}</div><div className="text-sm text-white/70 mt-2">{t("force_update_body")}</div></div>}
          </div>
          <div className="mt-5 text-xs text-white/60">{t("force_update_installed")} 1.34 · {t("force_update_required")} {form.min_ios || form.min_android || "—"}</div>
          <div className="mt-5 w-full max-w-sm h-14 rounded-2xl bg-amber-400 text-amber-950 font-bold text-lg flex items-center justify-center">{t("force_update_cta")}</div>
          <div className="mt-6 text-[11px] text-white/40">{t("force_update_preview_close")}</div>
        </div>
      )}
    </Card>
  );
};
